// apps/client/src/world/interior.ts (M1-T14) — the toy-room shell every interior stands in: a port of look-demo `room.ts` buildRoom
// (§8.4). Two rounded walls (back and left, so the camera sees in), a plank plinth, wallpaper and floor painted from `PatternSpec`s
// through the injectable painter, skirting, a curtained window, a doormat at the door, the decorate grid and footprint square, and
// warm lights. Furniture, residents and NPCs are added by the space modules (shop M1-T14, rooms M1-T16, museum M2-T10, M2-T11).
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { tuning, type PatternSpec } from '@whiskerton/content';
import { paintPattern } from '../assets/builders/surface';
import { lightingAt, NIGHT } from '../engine/lighting';

export interface RoomBuildOptions { paint?: (spec: PatternSpec, sizePx: number) => THREE.Texture }
export interface RoomSpec { w: number; h: number; wallpaper: PatternSpec; floor: PatternSpec; wallHeight?: number; window?: boolean }
export interface BuiltRoom {
  group: THREE.Group; lights: THREE.Light[]; grid: THREE.Object3D; foot: THREE.Mesh;
  setWallpaper(spec: PatternSpec): void; setFloor(spec: PatternSpec): void; dispose(): void;
  /** Light and look (2026-10-16): lights the room for the town clock (its space's tick calls it every frame). */
  light(minuteOfDay: number): void;
}
const I = tuning.light.interior;
const C = (hex: string) => new THREE.Color(hex);
const DAY = { hemiSky: C(I.day.hemiSky), hemiGround: C(I.day.hemiGround), key: C(I.day.key) };
const EVENING = { hemiSky: C(I.evening.hemiSky), hemiGround: C(I.evening.hemiGround), key: C(I.evening.key) };
/** How far into the evening a room is (0 day … 1 night): the clock's lit-window glow over the night's, so a room turns to its lamp
 *  exactly as the windows outside light up. */
export const eveningAt = (minuteOfDay: number): number => Math.min(1, Math.max(0, lightingAt(minuteOfDay).windowGlow / NIGHT.windowGlow));
// DESIGN.md "toy room" colours (presentation, not tuning).
const ROOM_WALL = '#F1E4CB', TRIM = '#FFF8EC', PLINTH = '#B98E63', CURTAIN = '#C9B3E6', RAIL = '#B08A63', MAT = '#C9A27E', SKY = '#BFE6F5';
/** How tall a room's back and left walls stand (the indoor camera frames their tops, world/camera.ts). */
export const WALL_HEIGHT = 3.3;

/** paintPattern, or, where there is no 2D canvas (happy-dom tests, a WebView without one), a flat texture in the pattern's base
 *  colour, so a room still builds. */
function defaultPaint(spec: PatternSpec, sizePx: number): THREE.Texture {
  try { return paintPattern(spec, sizePx); } catch {
    const n = parseInt(spec.base.slice(1), 16);
    const tex = new THREE.DataTexture(new Uint8Array([n >> 16, (n >> 8) & 255, n & 255, 255]), 1, 1);
    tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true;
    return tex;
  }
}
/** PlaneGeometry with UVs in metres, so a texture repeat of `scale` means `scale` repeats per metre. */
function metricPlane(w: number, h: number): THREE.PlaneGeometry {
  const g = new THREE.PlaneGeometry(w, h), uv = g.getAttribute('uv');
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * w, uv.getY(i) * h);
  return g;
}

export function buildRoomShell(spec: RoomSpec, opts: RoomBuildOptions = {}): BuiltRoom {
  const paint = opts.paint ?? defaultPaint;
  const { w: W, h: D } = spec, H = spec.wallHeight ?? WALL_HEIGHT;
  const group = new THREE.Group(); group.name = 'room';
  const mesh = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, name?: string) => {
    const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.castShadow = m.receiveShadow = true; if (name) m.name = name; group.add(m); return m;
  };
  const floorMat = new THREE.MeshStandardMaterial({ roughness: 0.75 });
  const floor = mesh(metricPlane(W, D).rotateX(-Math.PI / 2), floorMat, W / 2, 0, D / 2, 'floor');
  floor.castShadow = false;
  mesh(new RoundedBoxGeometry(W + 0.5, 0.3, D + 0.5, 3, 0.1), new THREE.MeshStandardMaterial({ color: PLINTH, roughness: 0.9 }), W / 2 - 0.1, -0.16, D / 2 - 0.1, 'plinth');
  const body = new THREE.MeshStandardMaterial({ color: ROOM_WALL, roughness: 0.95 });
  mesh(new RoundedBoxGeometry(W + 0.5, H + 0.3, 0.25, 3, 0.08), body, W / 2 - 0.1, H / 2 - 0.15, -0.125, 'wallBack');
  mesh(new RoundedBoxGeometry(0.25, H + 0.3, D + 0.5, 3, 0.08), body, -0.125, H / 2 - 0.15, D / 2 - 0.1, 'wallLeft');
  const backMat = new THREE.MeshStandardMaterial({ roughness: 0.95 }), sideMat = new THREE.MeshStandardMaterial({ roughness: 0.95 });
  mesh(metricPlane(W, H), backMat, W / 2, H / 2, 0.003, 'paperBack');
  mesh(metricPlane(D, H), sideMat, 0.003, H / 2, D / 2, 'paperLeft').rotation.y = Math.PI / 2;
  const trim = new THREE.MeshStandardMaterial({ color: TRIM, roughness: 0.8 });
  mesh(new RoundedBoxGeometry(W, 0.24, 0.07, 2, 0.02), trim, W / 2, 0.12, 0.035);
  mesh(new RoundedBoxGeometry(0.07, 0.24, D, 2, 0.02), trim, 0.035, 0.12, D / 2);
  if (spec.window !== false && W >= 4) {
    const wx = W * 0.7, wy = H * 0.63;
    mesh(new RoundedBoxGeometry(2.1, 1.6, 0.14, 2, 0.05), trim, wx, wy, 0.06, 'window');
    mesh(new THREE.PlaneGeometry(1.8, 1.3), new THREE.MeshBasicMaterial({ color: SKY }), wx, wy, 0.135, 'windowView').castShadow = false;
    mesh(new RoundedBoxGeometry(0.08, 1.3, 0.1, 2, 0.02), trim, wx, wy, 0.15);   // the window's cross bars
    mesh(new RoundedBoxGeometry(1.8, 0.08, 0.1, 2, 0.02), trim, wx, wy, 0.15);
    const curtain = new THREE.MeshStandardMaterial({ color: CURTAIN, roughness: 0.95 });
    for (const sd of [-1, 1]) mesh(new RoundedBoxGeometry(0.4, 2.0, 0.12, 2, 0.06), curtain, wx + sd * 1.32, wy - 0.05, 0.2);
    mesh(new RoundedBoxGeometry(3.3, 0.1, 0.1, 2, 0.04), new THREE.MeshStandardMaterial({ color: RAIL, roughness: 0.8 }), wx, wy + 1.05, 0.22);
  }
  // The door is on the south wall at tile x = floor(w / 2) (tuning.spaces and rules/house roomsForLevel): the mat sits just inside it.
  const mat = mesh(new RoundedBoxGeometry(1.4, 0.04, 0.8, 2, 0.02), new THREE.MeshStandardMaterial({ color: MAT, roughness: 1 }), Math.floor(W / 2) + 0.5, 0.02, D - 0.45, 'doormat');
  mat.castShadow = false;

  const lines: number[] = [];
  for (let x = 0; x <= W; x++) lines.push(x, 0.04, 0, x, 0.04, D);
  for (let z = 0; z <= D; z++) lines.push(0, 0.04, z, W, 0.04, z);
  const grid = new THREE.LineSegments(new THREE.BufferGeometry().setAttribute('position', new THREE.Float32BufferAttribute(lines, 3)),
    new THREE.LineBasicMaterial({ color: '#FFFBF1', transparent: true, opacity: 0.5, depthWrite: false }));
  grid.name = 'grid'; grid.visible = false; group.add(grid);
  const foot = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ color: '#3A7D44', transparent: true, opacity: 0.28, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4 }));
  foot.name = 'foot'; foot.renderOrder = 10; foot.visible = false; group.add(foot);

  // A warm key over a cooler fill and a warm lamp (tuning.light.interior), as outdoors; light() follows the clock.
  const key = new THREE.DirectionalLight(DAY.key, I.day.keyIntensity);
  key.position.set(W / 2 + 5, 12, D + 7); key.target.position.set(W / 2, 0, D / 2); key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  Object.assign(key.shadow.camera, { left: -10, right: 10, top: 10, bottom: -10, near: 1, far: 40 });
  key.shadow.normalBias = I.shadow.normalBias; key.shadow.radius = I.shadow.radius; key.shadow.intensity = I.shadow.intensity;
  const lamp = new THREE.PointLight(I.lamp.color, I.day.lamp, I.lamp.distance); lamp.position.set(W * 0.25, 2.4, D * 0.6);
  const hemi = new THREE.HemisphereLight(DAY.hemiSky, DAY.hemiGround, I.day.hemi);
  const lights: THREE.Light[] = [hemi, key, lamp];
  group.add(...lights, key.target);
  const view = group.getObjectByName('windowView') as THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | undefined;
  const light = (minuteOfDay: number) => {
    const t = eveningAt(minuteOfDay), n = (a: number, b: number) => a + (b - a) * t;
    hemi.color.copy(DAY.hemiSky).lerp(EVENING.hemiSky, t); hemi.groundColor.copy(DAY.hemiGround).lerp(EVENING.hemiGround, t);
    hemi.intensity = n(I.day.hemi, I.evening.hemi);
    key.color.copy(DAY.key).lerp(EVENING.key, t); key.intensity = n(I.day.keyIntensity, I.evening.keyIntensity);
    lamp.intensity = n(I.day.lamp, I.evening.lamp);
    if (view) { const l = lightingAt(minuteOfDay); view.material.color.copy(l.sky).lerp(l.horizon, 0.5); }
  };

  const setWallpaper = (s: PatternSpec) => {             // UVs are in metres, so one texture papers both walls at the same scale
    backMat.map?.dispose();
    backMat.map = sideMat.map = paint(s, 256);
    backMat.needsUpdate = sideMat.needsUpdate = true;
  };
  const setFloor = (s: PatternSpec) => { floorMat.map?.dispose(); floorMat.map = paint(s, 512); floorMat.needsUpdate = true; };
  setWallpaper(spec.wallpaper); setFloor(spec.floor);
  return {
    group, lights, grid, foot, setWallpaper, setFloor, light,
    dispose() {
      const mats = new Set<THREE.Material>();
      group.traverse((o) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
        const mat = m.material as THREE.Material | THREE.Material[] | undefined;
        if (mat) for (const x of Array.isArray(mat) ? mat : [mat]) mats.add(x);
      });
      for (const m of mats) { (m as THREE.MeshStandardMaterial).map?.dispose(); m.dispose(); }   // a shared map disposes twice: harmless
      key.shadow.map?.dispose();
    },
  };
}

export function interiorScene(spec: RoomSpec, opts?: RoomBuildOptions): { scene: THREE.Scene; room: BuiltRoom } {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#F3E4C6');
  const room = buildRoomShell(spec, opts);
  scene.add(room.group);
  return { scene, room };
}
